import { isRunningHour } from './channel-type';
import { CommunicationType } from './fu-unit';
import { SensorCableTag } from './sensor-cable-tag';
import { Slot, SlotOptionType, BoardType, Terminal, TerminalSide, terminalType } from './slot';

export const DIRows = 64;
export const DONormalRows = 32;
export const DORelayRows = 16;
export const AIRows = 16;
export const OutputRows = 8;
export const ThermalKRows = 16;
export const ComWireRows = 4;

export class FcuUnitPrint {
    fcuUnit: {
        id: string;
        vesselId: string;
        location: string;
        index: number;
        fcuSlotList: Slot[];
    };
    fuUnits: FuUnitPrint[];
}

export class FuUnitPrint {
    id: string;
    fcuId: string;
    vesselId: string;
    location: string;
    index: number;
    communicationType: CommunicationType;
    fieldNetLineAIp: string;
    fieldNetLineBIp: string;
    slotList: Slot[];
}

export function getBoardType(slotType: SlotOptionType) {
    switch (slotType) {
        case 'DI':
            return BoardType.M002;
        case 'DO':
            return BoardType.M003;
        case 'AI (4-20mA)':
            return BoardType.M400;
        case 'AI (1-5V)':
            return BoardType.M500;
        case 'AI (Pt100 2-wire)':
            return BoardType.M110;
        case 'AI (Pt100 3-wire)':
            return BoardType.M110;
        case 'AO (4-20mA)':
            return BoardType.M030;
        case 'AI (Thermal K)':
            return BoardType.M200;
        default:
            return;
    }
}

export function getTMType(slotType: SlotOptionType, outputTerminal?: Terminal) {
    let letter = '';
    if (outputTerminal?.side == TerminalSide.LEFT) letter = 'L';
    else if (outputTerminal?.side == TerminalSide.RIGHT) letter = 'R';

    switch (slotType) {
        case 'DI':
            return `DI-${letter}`;
        case 'DO':
            switch (outputTerminal.type) {
                case terminalType.normal:
                    switch (outputTerminal.side) {
                        case TerminalSide.LEFT:
                            return 'DO-L';
                        case TerminalSide.RIGHT:
                            return 'DO-R';
                        default:
                            return '';
                    }
                case terminalType.relay:
                    switch (outputTerminal.side) {
                        case TerminalSide.LEFT:
                            return 'RY-L';
                        case TerminalSide.RIGHT:
                            return 'RY-R';
                        default:
                            return '';
                    }
                default:
                    return '';
            }
        case 'AI (4-20mA)':
            return `42-${letter}-1`;
        case 'AI (1-5V)':
            return `15-${letter}`;
        case 'AI (Pt100 2-wire)':
            return `15-${letter}`;
        case 'AI (Pt100 3-wire)':
            return `TEMP-${letter}`;
        case 'AO (4-20mA)':
            return `AO-${letter}-1`;
        case 'AI (Thermal K)':
            return `TMK-${letter}`;
        default:
            return '';
    }
}

export function getDrawingSuffix(fcuNum: number, fuNum: number, slotNum: number, letter = '') {
    const number = 1 + slotNum + 8 * fuNum;
    const paddedNumber = number.toString().padStart(3, '0');
    const stringStart = `FU${fcuNum}${paddedNumber}`;

    return stringStart + letter;
}

export function formatDate(date: Date) {
    const yy = String(date.getFullYear()).slice(-2);
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${yy}/${mm}/${dd} ${hh}:${min}`;
}

export function getDOLetter(index: number, terminals: Terminal[]): string {
    if (index == 1) return 'A';
    if (index == 2) {
        if (terminals[1].type == terminalType.normal) {
            if (terminals[0].type == terminalType.normal) return 'B';
            if (terminals[0].type == terminalType.relay) return 'C';
        } else {
            if (terminals[0].type == terminalType.normal) return 'C';
            if (terminals[0].type == terminalType.relay) return 'B';
        }
    }
    if (index == 3) {
        if (terminals[0].type == terminalType.normal || terminals[1].type == terminalType.normal) {
            return 'D';
        } else return 'C';
    }
    return 'D';
}

export function getLetterByIndex(index, initialPosition = 2) {
    const position = 1 + initialPosition + (index - 1) * 4;
    let letter = '';
    let pos = position;

    while (pos >= 0) {
        letter = String.fromCharCode(65 + (pos % 26)) + letter;
        pos = Math.floor(pos / 26) - 1;
    }

    return letter;
}

export function getTerminalSensors(sensorCableTag: SensorCableTag[], pinNum: number) {
    const sensors = sensorCableTag.filter(item => Number(item.pinNum) === pinNum);
    const runningHourSensor = sensors.find(item => isRunningHour(item.channel?.fcuData?.channelType));
    const sourceSensor = sensors.find(item => item !== runningHourSensor);

    return { sourceSensor, runningHourSensor };
}

export function joinTerminalChannelTags(...tags: string[]) {
    return tags.filter(tag => tag).join('\n');
}

export function getStyle(alignment = 'left') {
    return {
        horizontalAlignment: alignment,
        shrinkToFit: true,
        verticalAlignment: 'middle',
        fill: { type: 'solid', color: { rgb: 'ffffff' } },
    };
}
